import type {
  CrmOrganizationGeoResolveResult,
  CrmOrganizationListFilters,
  CrmOrganizationMutation,
  CrmOrganizationSiteGeoStatus,
  CrmOrganizationSiteItem,
  CrmOrganizationSiteMutation,
} from "../types";
import type { GeoBusinessVerifyResult, GeoOperationStatus } from "../../../shared/business-geo";
import {
  getCityOptions,
  getDistrictOptions,
  getProvinceOptions,
  normalizeProvinceName,
} from "../../../shared/region";

const crmOrganizationCollegeType = "高校";

/** 归一化学校组织筛选条件。 */
export function normalizeCrmOrganizationFilters(
  filters: CrmOrganizationListFilters,
): CrmOrganizationListFilters {
  return {
    keyword: normalizeOptionalText(filters.keyword),
    orgType: normalizeOptionalText(filters.orgType),
    province: normalizeOptionalText(filters.province),
    city: normalizeOptionalText(filters.city),
    district: normalizeOptionalText(filters.district),
    isDisabled: filters.isDisabled,
  };
}

/** 归一化学校组织保存载荷。 */
export function normalizeCrmOrganizationMutation(
  payload: CrmOrganizationMutation,
): CrmOrganizationMutation {
  const orgType = payload.orgType.trim() || crmOrganizationCollegeType;
  return {
    orgName: payload.orgName.trim(),
    orgType,
    orgSubtype:
      orgType === crmOrganizationCollegeType ? normalizeOptionalText(payload.orgSubtype) : undefined,
    province: normalizeOptionalText(payload.province),
    city: normalizeOptionalText(payload.city),
    district: normalizeOptionalText(payload.district),
    address: normalizeOptionalText(payload.address),
    isDisabled: payload.isDisabled,
    remark: normalizeOptionalText(payload.remark),
  };
}

/** 根据 GEO 识别结果回填校区表单。 */
export function applyGeoResolveResultToSiteMutation(
  payload: CrmOrganizationSiteMutation,
  result: CrmOrganizationGeoResolveResult,
): CrmOrganizationSiteMutation {
  return {
    ...payload,
    province: result.province || payload.province,
    city: result.city || payload.city,
    district: result.district || payload.district,
    address: result.navigationAddress || payload.address,
    lng: result.longitude ?? payload.lng,
    lat: result.latitude ?? payload.lat,
  };
}

/** 采用高德候选回填校区表单。 */
export function applyGeoCandidateToSiteMutation(
  payload: CrmOrganizationSiteMutation,
  candidate: {
    province: string;
    city: string;
    district: string;
    address: string;
    name: string;
    longitude: number;
    latitude: number;
  },
): CrmOrganizationSiteMutation {
  return {
    ...payload,
    province: candidate.province || payload.province,
    city: candidate.city || payload.city,
    district: candidate.district || payload.district,
    address: candidate.address || candidate.name || payload.address,
    lng: candidate.longitude,
    lat: candidate.latitude,
  };
}

/** 采用高德候选回填学校组织省市区，并与本地行政区划事实源对齐。 */
export function applyGeoCandidateToOrganizationMutation(
  payload: CrmOrganizationMutation,
  candidate: {
    province: string;
    city: string;
    district: string;
    address: string;
    name: string;
  },
): CrmOrganizationMutation {
  return applyGeoResolveResultToOrganizationMutation(payload, {
    province: candidate.province,
    city: candidate.city,
    district: candidate.district,
    town: "",
    coordinateSystem: "GCJ-02",
    navigationAddress: candidate.address || candidate.name,
    resolvedLevel: "area",
    confidenceScore: 0,
    needsReview: false,
    message: "",
    ambiguityNotes: "",
    candidates: [],
  });
}

/** 根据本地行政区划事实源校准高德结果并回填学校组织表单。 */
export function applyGeoResolveResultToOrganizationMutation(
  payload: CrmOrganizationMutation,
  result: CrmOrganizationGeoResolveResult,
): CrmOrganizationMutation {
  const province = normalizeProvinceName(result.province);
  if (!province || !getProvinceOptions().includes(province)) {
    return payload;
  }

  const city = findRegionOption(getCityOptions(province), result.city, ["市"]);
  if (!city) {
    return { ...payload, province, city: "", district: "" };
  }

  const district = findRegionOption(
    getDistrictOptions(province, city),
    result.district,
    ["区", "县", "市"],
  );
  if (!district) {
    return { ...payload, province, city, district: "" };
  }

  return {
    ...payload,
    province,
    city,
    district,
    address: payload.address || result.navigationAddress || "",
  };
}

/** 当前城市没有区县事实数据时，区字段回退为文本输入。 */
export function shouldUseManualDistrictInput(province?: string, city?: string) {
  if (!province || !city) {
    return false;
  }
  return getDistrictOptions(province, city).length === 0;
}

/** 按当前学校与校区名组装 GEO 识别名称；详细地址单独走 sourceAddress，避免重复拼进检索词。 */
export function buildGeoResolveAreaName(
  payload: CrmOrganizationSiteMutation,
  organizationName?: string,
) {
  const normalizedOrganizationName = normalizeOptionalText(organizationName);
  const normalizedSiteName = normalizeOptionalText(payload.siteName);
  if (normalizedOrganizationName && normalizedSiteName) {
    return `${normalizedOrganizationName}${normalizedSiteName}`;
  }
  return normalizedSiteName ?? normalizedOrganizationName ?? "";
}

/** 仅业务页 GEO 确认并保存后视为已识别。 */
export function hasSavedCrmOrganizationSiteGeo(
  site: Pick<CrmOrganizationSiteItem, "geoStatus" | "lng" | "lat">,
): boolean {
  return site.geoStatus === "confirmed" && site.lng != null && site.lat != null;
}

/** 按本次抽屉 GEO 面板状态解析应写入的持久化 GEO 状态。 */
export function resolvedCrmOrganizationSiteGeoStatus(
  payload: Pick<CrmOrganizationSiteMutation, "lng" | "lat">,
  geoStatus: GeoOperationStatus,
  geoAdopted = false,
): CrmOrganizationSiteGeoStatus {
  const confirmedByPanel = geoStatus === "confirmed" || geoAdopted;
  return confirmedByPanel && payload.lng != null && payload.lat != null
    ? "confirmed"
    : "unverified";
}

/** 用已保存地址和坐标还原抽屉中的已确认 GEO 面板。 */
export function savedCrmOrganizationSiteGeoResult(
  site: CrmOrganizationSiteItem,
): GeoBusinessVerifyResult | undefined {
  if (!hasSavedCrmOrganizationSiteGeo(site)) return undefined;
  return {
    scenario: "school_campus",
    status: "confirmed",
    conclusion: "已保存高德标准地址与坐标",
    llmConclusion: "已保存高德标准地址与坐标",
    amapStandardAddress: site.address ?? "",
    province: site.province ?? "",
    city: site.city ?? "",
    district: site.district ?? "",
    longitude: site.lng,
    latitude: site.lat,
    coordinateSystem: "GCJ-02",
    confidenceScore: 0,
    correctionReason: "",
    retryable: false,
    candidates: [],
  };
}

/** 归一化可选查询文本。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 忽略常见行政区后缀匹配本地标准名称。 */
function findRegionOption(options: string[], value: string | undefined, suffixes: string[]) {
  const normalizedValue = normalizeRegionName(value, suffixes);
  return options.find((option) => normalizeRegionName(option, suffixes) === normalizedValue);
}

/** 归一行政区名称用于高德结果与本地事实源匹配。 */
function normalizeRegionName(value: string | undefined, suffixes: string[]) {
  let normalized = value?.replace(/\s+/g, "").trim() ?? "";
  const suffix = suffixes.find((candidate) => normalized.endsWith(candidate));
  if (suffix) {
    normalized = normalized.slice(0, -suffix.length);
  }
  return normalized;
}
